import React from 'react';
import CampaignInsightsTable from '../campaignManager/CampaignInsightsTable';
import type { Campaign } from './demoData';
import type { PortfolioMetricsRow } from '@/lib/api/services/portfolioMetricsService';

interface PortfolioTableProps {
  rows: PortfolioMetricsRow[];
  loading?: boolean;
  errorText?: string | null;
  onPortfolioClick?: (portfolioId: string) => void;
}

const PortfolioTable: React.FC<PortfolioTableProps> = ({
  rows,
  loading,
  errorText,
  onPortfolioClick,
}) => {
  const formatCurrency = (value: number) => {
    if (!value) return '₹0';
    if (value >= 1_000_000) return `₹${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `₹${(value / 1_000).toFixed(2)}K`;
    return `₹${value.toFixed(2)}`;
  };

  const formatNumber = (value: number) => {
    if (!value) return '0';
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
    return value.toLocaleString();
  };

  const formatPercent = (value: number) =>
    `${(value || 0).toFixed(2)}%`;

  const mappedRows: Campaign[] = rows.map((row) => ({
    id: row.portfolio_id,
    name: row.portfolio_name || row.portfolio_id,
    isActive: true,
    association: 'associated',
    servingStatus: 'Delivering',
    targetingType: 'Manual',
    dailyBudget: '₹0',
    budgetUtilization: '0%',
    impressions: formatNumber(row.impressions || 0),
    impressionsTrend: 'up',
    clicks: formatNumber(row.clicks || 0),
    clicksTrend: 'up',
    sales: formatCurrency(row.sales || 0),
    salesTrend: 'up',
    acos: formatPercent(row.acos || 0),
    acosTrend: 'up',
    roas:
      row.spend && row.spend > 0
        ? ((row.sales || 0) / row.spend).toFixed(2)
        : '0.00',
    roasTrend: 'up',
    cvr: formatPercent(row.cvr || 0),
    cvrTrend: 'up',
  }));

  // Handle row click
  const handleRowClick = (record: Campaign) => {
    if (onPortfolioClick) {
      onPortfolioClick(record.id);
    }
  };

  return (
    <CampaignInsightsTable
      data={mappedRows}
      loading={loading}
      errorText={errorText}
      total={rows.length}
      onRowClick={onPortfolioClick ? handleRowClick : undefined}
    />
  );
};

export default PortfolioTable;
